import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getLocale } from 'umi';
import EventWindows from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventWindows';
import '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventWindows/index.scss';
import type { IChartEventSetting } from '@/pages/dashboard/filterPageComponents/overview/KpiEventFilter';
import type { IChartOverlaySlotContext } from '@/pages/dashboard/pages/overview/components/OverviewMetricsChart/type';
import type { EventDataMap, TEventTypeBarColorMapConfig } from '@/pages/dashboard/components/EventMetricTrend/types';
import { applyChartEventWindowsPipelineToEventDataMap } from '@/pages/dashboard/components/EventMetricTrend/chartEventWindowsPipeline';
import { resolveEventTypeCollapseConfig } from '@/pages/dashboard/components/EventMetricTrend/utils';
import {
  alignEventDataMapKeysToXAxis,
  buildEventListMapForWindows,
  resolveOverviewEventDateFormat,
  resolveOverviewEventDateType,
  type TOverviewEventDateType,
} from './eventModalDataUtils';
import { LANGUAGE_EN } from '@home/_config/config';

interface IFsEventWindowsProps extends IChartOverlaySlotContext {
  active: boolean;
  eventData: unknown[];
  chartEventSetting: IChartEventSetting;
  bubbleFilterCommitted?: boolean;
  eventTypeMaxCount?: number;
  onXAxisDataChange?: (xAxisData: string[]) => void;
  eventTypeBarColorMap?: TEventTypeBarColorMapConfig;
  eventDetailsNav?: {
    minCountExclusive: number;
    onNavigate: (item: unknown, xAxisValue: string) => void;
    shouldAllowDetailsNav?: (xAxisValue: string) => boolean;
  };
  /** 与筛选 timeType 对齐；默认按 Day */
  eventDateType?: TOverviewEventDateType;
  eventDateFormat?: string;
}

/**
 * 全屏 EventWindows：与 KPI EventMetricTrend 同思路
 * - TopTipsEvent 由 chartItem 在 setOtherConfigFunc 写入 option（不在此 setOption）
 * - getChart / xAxisData / layoutKey 由 chartItem 提供
 * - layoutKey 变化时 remount EventWindows，让 EventItem 重新 convertToPixel
 */
export default function FsEventWindows(props: IFsEventWindowsProps) {
  const {
    active,
    eventData,
    chartEventSetting,
    bubbleFilterCommitted = false,
    getChart,
    xAxisData,
    chartLoading,
    layoutKey,
    eventTypeMaxCount = 0,
    onXAxisDataChange,
    eventTypeBarColorMap,
    eventDetailsNav,
    eventDateType: eventDateTypeProp,
    eventDateFormat: eventDateFormatProp,
  } = props;

  const eventDateType =
    eventDateTypeProp ?? resolveOverviewEventDateType('Day');
  const eventDateFormat =
    eventDateFormatProp ?? resolveOverviewEventDateFormat('Day');

  const containerRef = useRef<HTMLDivElement>(null);
  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    onXAxisDataChange?.(active ? xAxisData : []);
  }, [active, xAxisData, onXAxisDataChange]);

  /** 等图表 setOption 完成后再挂 EventWindows（避免 convertToPixel 落到 left:0） */
  useEffect(() => {
    if (!active || chartLoading) {
      setChartReady(false);
      return;
    }
    const chart = getChart();
    if (!chart) {
      setChartReady(false);
      return;
    }
    let settled = false;
    const markReady = (): void => {
      if (settled) {
        return;
      }
      settled = true;
      setChartReady(true);
    };
    const onFinished = (): void => {
      chart.off('finished', onFinished);
      markReady();
    };
    chart.on('finished', onFinished);
    const fallbackId = window.setTimeout(markReady, 600);
    return () => {
      settled = true;
      chart.off('finished', onFinished);
      window.clearTimeout(fallbackId);
      setChartReady(false);
    };
  }, [active, chartLoading, layoutKey, getChart]);

  const isEN = getLocale() === LANGUAGE_EN;
  const experimentCollapseConfig = useMemo(
    () =>
      resolveEventTypeCollapseConfig(undefined, {
        useKpiExperimentMatchDefaults: true,
      }),
    [],
  );

  const eventListMap = useMemo<EventDataMap>(() => {
    let raw = buildEventListMapForWindows(
      eventData,
      chartEventSetting.bubbleEvent ?? {},
      eventTypeMaxCount,
      eventTypeBarColorMap,
      bubbleFilterCommitted,
    );
    raw = applyChartEventWindowsPipelineToEventDataMap(raw, {
      mergeConsecutiveTrendDays: true,
      eventTypeCollapseConfig: experimentCollapseConfig,
      showExperimentEventDetailFlat:
        chartEventSetting.showExperimentEventDetailFlat,
      isEn: isEN,
      experimentFixedLabel: {
        enabled: true,
        labelCn: 'A/B 实验',
        labelEn: 'A/B Test',
      },
      applyBarColorMap: false,
    }) ?? raw;
    return alignEventDataMapKeysToXAxis(raw, xAxisData, {
      dateType: eventDateType,
    });
  }, [
    eventData,
    chartEventSetting.bubbleEvent,
    bubbleFilterCommitted,
    chartEventSetting.showExperimentEventDetailFlat,
    eventTypeMaxCount,
    eventTypeBarColorMap,
    experimentCollapseConfig,
    isEN,
    xAxisData,
    eventDateType,
  ]);

  if (!active || !chartEventSetting.showEventBubbles) {
    return null;
  }

  const chartInstance = chartReady && !chartLoading ? getChart() : null;
  const canShow =
    chartInstance &&
    xAxisData.length > 0 &&
    Object.keys(eventListMap).length > 0;

  return (
    <div ref={containerRef} className="dbd-overview-metrics-trend-fs-event-windows">
      {canShow && (
        <EventWindows
          key={layoutKey}
          myChart={chartInstance}
          eventList={eventListMap}
          format={eventDateFormat}
          maxTimeValue={xAxisData[xAxisData.length - 1] ?? ''}
          showEventAxis={!!chartEventSetting.showEventAxis}
          dateType={eventDateType}
          xAxisData={xAxisData}
          yAxisMin
          eventDetailsNav={eventDetailsNav}
        />
      )}
    </div>
  );
}
